import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Briefcase, Trash2, Edit2, User } from "lucide-react";
import {
  getPlacementsAPI,
  createPlacementAPI,
  updatePlacementAPI,
  deletePlacementAPI,
} from "../services/apicollection";
import PlacementForm from "../Components/PlacementForm";

const DEFAULT_PROFILE =
  "https://res.cloudinary.com/dz1qj3x2h/image/upload/v1735681234/default-profile.png";

const PlacementManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const controller = new AbortController();
  const fetchPlacements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPlacementsAPI({ signal: controller.signal });
      const payload = res?.data;
      if (res?.status && payload?.placements) {
        setPlacements(payload.placements);
      } else {
        setPlacements([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load placements");
      setPlacements([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlacements();  
    return () => {
      controller.abort();
    };
  }, [fetchPlacements]);

  const handleSave = () => {
    setShowForm(false);
    setEditingPlacement(null);
    fetchPlacements();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPlacement(null);
  };

  const handleToggle = async (placement) => {
    setTogglingId(placement._id);
    try {
      const res = await updatePlacementAPI(placement._id, {
        isEnabled: !placement.isEnabled,
      });
      if (res?.status) {
        fetchPlacements();
      } else {
        alert(res?.message || "Failed to update");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this placement?")) return;
    try {
      const res = await deletePlacementAPI(id);
      if (res?.status) {
        fetchPlacements();
      } else {
        alert(res?.message || "Failed to delete");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete");
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <PlacementForm onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  if (editingPlacement) {
    return (
      <PlacementEditForm
        placement={editingPlacement}
        onSuccess={handleSave}
        onCancel={() => setEditingPlacement(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Placements</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Add Placement
        </button>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-medium  first-letter:uppercase tracking-wide text-slate-500">
          Placements shown on home page and /placements
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
            {error}
          </div>
        ) : placements.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
            <Briefcase size={36} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No placements yet</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
            >
              Add your first placement
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {placements.map((p) => (
              <article
                key={p._id}
                className={`flex flex-col overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow ${
                  p.isEnabled ? "border-[#1d3350] bg-[#0f1c2e]" : "border-[#1d3350] bg-[#0d1b2e] opacity-75"
                }`}
              >
                <div className="flex items-center gap-3 p-3">
                  <img
                    src={p.profile || DEFAULT_PROFILE}
                    alt={p.studentName}
                    className="h-14 w-14 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-semibold text-slate-100">
                      {p.studentName}
                    </h4>
                    <p className="truncate text-xs text-slate-400">{p.companyName}</p>
                    {p.package && (
                      <p className="text-xs font-medium text-cyan-600">{p.package}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-[#17283f] px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2 text-xs">
                    <span className="text-slate-400">Show on site:</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={p.isEnabled}
                      onClick={() => handleToggle(p)}
                      disabled={togglingId === p._id}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none ${
                        p.isEnabled ? "bg-cyan-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-[#0f1c2e] transition-transform ${
                          p.isEnabled ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                    <span className="font-medium">{p.isEnabled ? "Yes" : "No"}</span>
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setEditingPlacement(p)}
                      className="rounded border border-[#1d3350] px-2 py-1 text-xs text-slate-400 hover:bg-[#0d1b2e]"
                    >
                      <Edit2 size={12} className="inline" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(p._id)}
                      className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={12} className="inline" /> Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const PlacementEditForm = ({ placement, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    studentName: placement?.studentName || "",
    companyName: placement?.companyName || "",
    package: placement?.package || "",
  });
  const [profilePreview, setProfilePreview] = useState(placement?.profile || null);
  const [profileFile, setProfileFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setProfilePreview(ev.target?.result);
      setProfileFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.studentName?.trim() || !formData.companyName?.trim()) {
      alert("Student name and company name are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        studentName: formData.studentName.trim(),
        companyName: formData.companyName.trim(),
        package: formData.package?.trim() || "",
        profileFile,
      };
      const res = await updatePlacementAPI(placement._id, payload);
      if (!res?.status) {
        alert(res?.message || "Failed to update");
      } else {
        onSuccess();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mx-auto max-w-lg w-full rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-lg sm:p-6">
        <h3 className="mb-4 text-base font-semibold text-slate-200">Edit Placement</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400">Student name *</label>
            <input
              type="text"
              value={formData.studentName}
              onChange={(e) => handleChange("studentName", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">Company name *</label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">Package (optional)</label>
            <input
              type="text"
              value={formData.package}
              onChange={(e) => handleChange("package", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">Profile photo</label>
            <div className="mt-1 flex items-center gap-3">
              <img
                src={profilePreview || DEFAULT_PROFILE}
                alt="Profile"
                className="h-16 w-16 rounded-full object-cover"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-medium text-slate-400 underline"
              >
                Change
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfileChange}
                className="hidden"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlacementManagementPage;
